package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.BulkEpisodeImportResponse
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.files.FileList
import org.w3c.files.get

@Composable
fun BulkEpisodeImportForm(
    apiClient: ApiClient,
    onImportComplete: (BulkEpisodeImportResponse) -> Unit = {},
    onCancel: (() -> Unit)? = null
) {
    val colors = AppTheme.colors
    var selectedFiles by remember { mutableStateOf<FileList?>(null) }
    var isUploading by remember { mutableStateOf(false) }
    var uploadProgress by remember { mutableStateOf(0f) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var importReport by remember { mutableStateOf<BulkEpisodeImportResponse?>(null) }
    var detectedAudiobooks by remember { mutableStateOf<Map<String, Int>>(emptyMap()) }

    val scope = rememberCoroutineScope()

    // Resize image if it exceeds 500KB
    suspend fun resizeImageIfNeeded(file: org.w3c.files.File): org.w3c.files.File {
        val maxSizeBytes = 500 * 1024 // 500KB

        // If file is not an image or is already small enough, return as-is
        if (!file.type.startsWith("image/") || file.size.toLong() <= maxSizeBytes) {
            return file
        }

        return try {
            // Create image element
            val img = org.w3c.dom.Image()
            val imageUrl = org.w3c.dom.url.URL.createObjectURL(file)

            // Load image
            val loadPromise = kotlin.js.Promise<org.w3c.dom.Image> { resolve, reject ->
                img.onload = { resolve(img); null }
                img.onerror = { _, _, _, _, _ -> reject(Exception("Failed to load image")); null }
                img.src = imageUrl
            }

            val loadedImg = loadPromise.await()

            // Calculate new dimensions (max 1920x1920 while maintaining aspect ratio)
            val maxDimension = 1920
            var width = loadedImg.width
            var height = loadedImg.height

            if (width > maxDimension || height > maxDimension) {
                if (width > height) {
                    height = (height * maxDimension / width)
                    width = maxDimension
                } else {
                    width = (width * maxDimension / height)
                    height = maxDimension
                }
            }

            // Create canvas and draw resized image
            val canvas = window.document.createElement("canvas") as org.w3c.dom.HTMLCanvasElement
            canvas.width = width
            canvas.height = height

            val ctx = canvas.getContext("2d") as org.w3c.dom.CanvasRenderingContext2D
            ctx.drawImage(loadedImg, 0.0, 0.0, width.toDouble(), height.toDouble())

            // Try different quality levels until under 500KB
            var quality = 0.85
            var blob: org.w3c.files.Blob? = null

            while (quality > 0.3) {
                val blobPromise = kotlin.js.Promise<org.w3c.files.Blob> { resolve, _ ->
                    canvas.toBlob({ b: org.w3c.files.Blob? ->
                        if (b != null) resolve(b)
                    }, "image/jpeg", quality)
                }

                blob = blobPromise.await()

                if (blob != null && blob.size.toLong() <= maxSizeBytes) {
                    break
                }

                quality -= 0.1
            }

            // Cleanup
            org.w3c.dom.url.URL.revokeObjectURL(imageUrl)

            // Create new File from blob
            if (blob != null && blob.size.toLong() <= maxSizeBytes) {
                val fileName = file.name.replace(Regex("\\.(png|jpg|jpeg)$", RegexOption.IGNORE_CASE), ".jpg")
                org.w3c.files.File(arrayOf(blob), fileName, org.w3c.files.FilePropertyBag(type = "image/jpeg"))
            } else {
                file // Return original if resizing failed
            }
        } catch (e: Exception) {
            console.error("Failed to resize image: ${file.name}", e)
            file // Return original on error
        }
    }

    // Parse files to detect audiobooks
    LaunchedEffect(selectedFiles) {
        selectedFiles?.let { files ->
            val audiobookFiles = mutableMapOf<String, Int>()

            for (i in 0 until files.length) {
                val file = files.item(i)
                if (file != null) {
                    val filenameWithoutExt = file.name.substringBeforeLast(".")

                    when {
                        // MP3 files: XXX_ISBN.mp3 pattern
                        file.name.endsWith(".mp3", ignoreCase = true) -> {
                            val parts = filenameWithoutExt.split("_")
                            if (parts.size >= 2) {
                                val isbn = parts.drop(1).joinToString("_")
                                audiobookFiles[isbn] = (audiobookFiles[isbn] ?: 0) + 1
                            }
                        }
                        // Image files: ISBN.jpg or ISBN.png pattern
                        file.name.endsWith(".jpg", ignoreCase = true) ||
                        file.name.endsWith(".jpeg", ignoreCase = true) ||
                        file.name.endsWith(".png", ignoreCase = true) -> {
                            // For images, the entire filename (without extension) is the ISBN
                            if (filenameWithoutExt.isNotBlank()) {
                                // Don't increment count for images, just ensure ISBN is in the map
                                if (!audiobookFiles.containsKey(filenameWithoutExt)) {
                                    audiobookFiles[filenameWithoutExt] = 0
                                }
                            }
                        }
                    }
                }
            }

            detectedAudiobooks = audiobookFiles
        }
    }

    fun performImport() {
        if (selectedFiles == null) return

        scope.launch {
            isUploading = true
            errorMessage = null
            try {
                // Process files and resize images before uploading
                val processedFiles = mutableListOf<org.w3c.files.File>()
                val files = selectedFiles!!

                for (i in 0 until files.length) {
                    val file = files.item(i)
                    if (file != null) {
                        val processedFile = if (file.type.startsWith("image/")) {
                            // Resize image if needed
                            resizeImageIfNeeded(file)
                        } else {
                            // Keep MP3 files as-is
                            file
                        }
                        processedFiles.add(processedFile)
                    }
                }

                // Upload processed files using ApiClient
                uploadProgress = 0f
                val result = apiClient.bulkImportEpisodes(processedFiles) { fraction ->
                    uploadProgress = fraction
                }
                importReport = result
                onImportComplete(result)
            } catch (e: Exception) {
                errorMessage = friendlyUploadError(e.message)
                console.error("Bulk episode import failed", e)
            } finally {
                isUploading = false
            }
        }
    }

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
            maxWidth(700.px)
            themeTransition()
        }
    }) {
        // Title
        H2({
            style {
                fontSize(Typography.xl)
                fontWeight("600")
                color(Color(colors.text))
                marginBottom(Spacing.lg)
            }
        }) {
            Text("Bulk Import Episodes")
        }

        // Description
        P({
            style {
                fontSize(Typography.base)
                color(Color(colors.textMuted))
                marginBottom(Spacing.xxl)
                property("line-height", "1.5")
            }
        }) {
            Text("Upload episode MP3 files and cover images for multiple audiobooks at once. Files must be named with the pattern: ")
            Code({
                style {
                    backgroundColor(Color(colors.surfaceHover))
                    padding(2.px, 6.px)
                    borderRadius(BorderRadius.sm)
                    fontSize(13.px)
                    fontFamily("monospace")
                }
            }) {
                Text("XXX_ISBN.mp3")
            }
            Text(" for episodes where XXX is the episode number (e.g., 001, 002) and ISBN identifies the audiobook. For cover images, name them: ")
            Code({
                style {
                    backgroundColor(Color(colors.surfaceHover))
                    padding(2.px, 6.px)
                    borderRadius(BorderRadius.sm)
                    fontSize(13.px)
                    fontFamily("monospace")
                }
            }) {
                Text("ISBN.jpg")
            }
            Text(" or ")
            Code({
                style {
                    backgroundColor(Color(colors.surfaceHover))
                    padding(2.px, 6.px)
                    borderRadius(BorderRadius.sm)
                    fontSize(13.px)
                    fontFamily("monospace")
                }
            }) {
                Text("ISBN.png")
            }
        }

        // File selection
        if (importReport == null) {
            FormField("Episode Files & Cover Images") {
                Input(InputType.File) {
                    attr("accept", ".mp3,.jpg,.jpeg,.png")
                    attr("multiple", "true")
                    if (isUploading) {
                        disabled()
                    }
                    onChange { event ->
                        val files = event.target.files
                        if (files != null && files.length > 0) {
                            selectedFiles = files
                            errorMessage = null
                        }
                    }
                }
            }

            // Detected audiobooks preview
            if (detectedAudiobooks.isNotEmpty()) {
                Div({
                    style {
                        backgroundColor(Color(colors.surfaceHover))
                        padding(Spacing.lg)
                        borderRadius(BorderRadius.md)
                        marginTop(Spacing.lg)
                        marginBottom(Spacing.lg)
                        property("border", "1px solid ${colors.border}")
                    }
                }) {
                    Div({
                        style {
                            fontSize(Typography.base)
                            fontWeight("600")
                            color(Color(colors.text))
                            marginBottom(Spacing.md)
                        }
                    }) {
                        Text("Detected Audiobooks (${detectedAudiobooks.size})")
                    }

                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            property("flex-direction", "column")
                            gap(Spacing.sm)
                            maxHeight(200.px)
                            overflowY("auto")
                        }
                    }) {
                        detectedAudiobooks.forEach { (isbn, count) ->
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    justifyContent(JustifyContent.SpaceBetween)
                                    alignItems(AlignItems.Center)
                                    padding(Spacing.sm, Spacing.md)
                                    backgroundColor(Color(colors.surface))
                                    borderRadius(BorderRadius.sm)
                                    fontSize(13.px)
                                }
                            }) {
                                Span({
                                    style {
                                        color(Color(colors.text))
                                        fontFamily("monospace")
                                    }
                                }) {
                                    Text(isbn)
                                }
                                Span({
                                    style {
                                        color(Color(colors.textMuted))
                                        fontSize(Typography.sm)
                                    }
                                }) {
                                    Text(if (count > 0) {
                                        "$count episode${if (count != 1) "s" else ""}"
                                    } else {
                                        "cover image only"
                                    })
                                }
                            }
                        }
                    }
                }
            }

            // Selected files summary
            selectedFiles?.let { files ->
                Div({
                    style {
                        backgroundColor(Color(if (colors == LightColors) "#eff6ff" else "#0c1929"))
                        padding(Spacing.md)
                        borderRadius(BorderRadius.md)
                        marginBottom(Spacing.lg)
                        property("border", "1px solid ${if (colors == LightColors) "#bfdbfe" else "#1e3a5f"}")
                    }
                }) {
                    var mp3Count = 0
                    var imageCount = 0
                    var totalSize = 0.0

                    for (i in 0 until files.length) {
                        val file = files.item(i)
                        if (file != null) {
                            totalSize += file.size.toDouble()
                            when {
                                file.name.endsWith(".mp3", ignoreCase = true) -> mp3Count++
                                file.name.endsWith(".jpg", ignoreCase = true) ||
                                file.name.endsWith(".jpeg", ignoreCase = true) ||
                                file.name.endsWith(".png", ignoreCase = true) -> imageCount++
                            }
                        }
                    }

                    Div({
                        style {
                            fontSize(Typography.base)
                            color(Color(if (colors == LightColors) "#1e40af" else "#93c5fd"))
                        }
                    }) {
                        Text("$mp3Count MP3 file${if (mp3Count != 1) "s" else ""}, $imageCount image${if (imageCount != 1) "s" else ""} selected (${formatFileSize(totalSize)})")
                    }
                }
            }

            // Upload progress
            if (isUploading) {
                Div({
                    style {
                        marginBottom(Spacing.lg)
                    }
                }) {
                    // Header
                    Div({
                        style {
                            fontSize(Typography.base)
                            color(Color(colors.text))
                            marginBottom(Spacing.sm)
                            fontWeight("500")
                        }
                    }) {
                        Text("Uploading ${selectedFiles?.length ?: 0} files... please wait")
                    }

                    // Real progress bar
                    val pct = (uploadProgress * 100).toInt()
                    Div({
                        style {
                            width(100.percent)
                            height(8.px)
                            backgroundColor(Color(colors.border))
                            borderRadius(4.px)
                            overflow("hidden")
                            marginBottom(Spacing.sm)
                        }
                    }) {
                        Div({
                            style {
                                height(100.percent)
                                property("width", "${pct}%")
                                backgroundColor(Color(colors.primary))
                                borderRadius(4.px)
                                property("transition", "width 0.2s ease")
                            }
                        })
                    }
                    Div({
                        style {
                            fontSize(Typography.sm)
                            color(Color(colors.textMuted))
                            marginBottom(Spacing.md)
                        }
                    }) { Text("${pct}% uploaded") }

                    // File list
                    Div({
                        style {
                            maxHeight(200.px)
                            overflowY("auto")
                            backgroundColor(Color(colors.surfaceHover))
                            borderRadius(BorderRadius.md)
                            padding(Spacing.md)
                        }
                    }) {
                        selectedFiles?.let { files ->
                            for (i in 0 until files.length) {
                                val file = files.item(i)
                                if (file != null) {
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex)
                                            alignItems(AlignItems.Center)
                                            gap(Spacing.sm)
                                            fontSize(13.px)
                                            marginBottom(4.px)
                                            color(Color(colors.textMuted))
                                        }
                                    }) {
                                        Span({
                                            style {
                                                property("min-width", "16px")
                                                display(DisplayStyle.InlineBlock)
                                                property("animation", "castly-spin 1s linear infinite")
                                            }
                                        }) { Text("\u21BB") }
                                        Style {
                                            "@keyframes castly-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }"
                                        }
                                        Span({
                                            style { property("word-break", "break-all") }
                                        }) {
                                            val sizeMB = (file.size.toDouble() / 1024 / 1024).asDynamic().toFixed(1)
                                            Text("${file.name} (${sizeMB} MB)")
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Error message
            errorMessage?.let { error ->
                Div({
                    style {
                        backgroundColor(Color(if (colors == LightColors) "#fef2f2" else "#450a0a"))
                        color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                        padding(Spacing.md)
                        borderRadius(BorderRadius.md)
                        marginBottom(Spacing.lg)
                        fontSize(Typography.base)
                        property("border", "1px solid ${if (colors == LightColors) "#fecaca" else "#7f1d1d"}")
                    }
                }) {
                    Text(error)
                }
            }

            // Action buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.md)
                    justifyContent(JustifyContent.End)
                }
            }) {
                onCancel?.let {
                    Button({
                        onClick { it() }
                        if (isUploading) disabled()
                        style {
                            padding(10.px, Spacing.xl)
                            fontSize(Typography.base)
                            fontWeight("500")
                            backgroundColor(Color(colors.surface))
                            color(Color(colors.text))
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                        }
                    }) {
                        Text("Cancel")
                    }
                }

                Button({
                    onClick { performImport() }
                    if (isUploading || selectedFiles == null || detectedAudiobooks.isEmpty()) {
                        disabled()
                    }
                    style {
                        padding(10.px, Spacing.xl)
                        fontSize(Typography.base)
                        fontWeight("500")
                        backgroundColor(if (isUploading || selectedFiles == null || detectedAudiobooks.isEmpty()) Color(colors.textMuted) else Color(colors.primary))
                        color(Color("#ffffff"))
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor(if (isUploading || selectedFiles == null || detectedAudiobooks.isEmpty()) "not-allowed" else "pointer")
                    }
                }) {
                    Text(if (isUploading) "Importing..." else "Import Episodes")
                }
            }
        } else {
            // Import report
            importReport?.let { report ->
                BulkImportReport(report, onCancel)
            }
        }
    }
}

@Composable
fun BulkImportReport(report: BulkEpisodeImportResponse, onClose: (() -> Unit)?) {
    val colors = AppTheme.colors
    val status = report.status
    val results = report.results
    val totalFiles = report.totalFiles
    val successfulFiles = report.successfulFiles
    val failedFiles = report.failedFiles
    val audiobookCount = report.audiobookCount

    Div({
        style {
            marginTop(Spacing.lg)
        }
    }) {
        // Overall status
        Div({
            style {
                padding(Spacing.lg)
                borderRadius(BorderRadius.lg)
                marginBottom(Spacing.lg)
                backgroundColor(when (status) {
                    "SUCCESS" -> Color(if (colors == LightColors) "#f0fdf4" else "#052e16")
                    "FAILED" -> Color(if (colors == LightColors) "#fef2f2" else "#450a0a")
                    else -> Color(if (colors == LightColors) "#fef9c3" else "#451a03")
                })
                property("border", "1px solid ${when (status) {
                    "SUCCESS" -> if (colors == LightColors) "#bbf7d0" else "#166534"
                    "FAILED" -> if (colors == LightColors) "#fecaca" else "#7f1d1d"
                    else -> if (colors == LightColors) "#fef08a" else "#854d0e"
                }}")
            }
        }) {
            H3({
                style {
                    fontSize(Typography.lg)
                    fontWeight("600")
                    marginBottom(Spacing.sm)
                    color(when (status) {
                        "SUCCESS" -> Color(if (colors == LightColors) "#166534" else "#86efac")
                        "FAILED" -> Color(if (colors == LightColors) "#991b1b" else "#fca5a5")
                        else -> Color(if (colors == LightColors) "#854d0e" else "#fcd34d")
                    })
                }
            }) {
                Text(when (status) {
                    "SUCCESS" -> "Import Successful"
                    "FAILED" -> "Import Failed"
                    else -> "Partial Import"
                })
            }

            Div({
                style {
                    fontSize(Typography.base)
                    color(Color(colors.text))
                    property("line-height", "1.5")
                }
            }) {
                Text("Processed $totalFiles files across $audiobookCount audiobooks")
                Br()
                Text("$successfulFiles succeeded, $failedFiles failed")
            }
        }

        // Per-audiobook results
        if (results.isNotEmpty()) {
            H4({
                style {
                    fontSize(15.px)
                    fontWeight("600")
                    color(Color(colors.text))
                    marginBottom(Spacing.md)
                }
            }) {
                Text("Results by Audiobook")
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    property("flex-direction", "column")
                    gap(Spacing.md)
                    maxHeight(300.px)
                    overflowY("auto")
                }
            }) {
                results.forEach { result ->
                    val resultStatus = result.status
                    val isbn = result.isbn
                    val title = result.title
                    val successCount = result.successCount
                    val failureCount = result.failureCount
                    val error = result.error

                    Div({
                        style {
                            padding(Spacing.md)
                            borderRadius(BorderRadius.md)
                            backgroundColor(Color(colors.surfaceHover))
                            property("border", "1px solid ${colors.border}")
                        }
                    }) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.SpaceBetween)
                                alignItems(AlignItems.Center)
                                marginBottom(Spacing.sm)
                            }
                        }) {
                            Div({
                                style {
                                    fontSize(Typography.base)
                                    fontWeight("500")
                                    color(Color(colors.text))
                                }
                            }) {
                                if (isbn != null) {
                                    Text(title ?: isbn)
                                } else {
                                    Text("Unmatched Files")
                                }
                            }

                            Span({
                                style {
                                    fontSize(Typography.sm)
                                    fontWeight("500")
                                    padding(Spacing.xs, Spacing.sm)
                                    borderRadius(BorderRadius.sm)
                                    backgroundColor(when (resultStatus) {
                                        "SUCCESS" -> Color(if (colors == LightColors) "#bbf7d0" else "#052e16")
                                        "FAILED" -> Color(if (colors == LightColors) "#fecaca" else "#450a0a")
                                        else -> Color(if (colors == LightColors) "#fef08a" else "#451a03")
                                    })
                                    color(when (resultStatus) {
                                        "SUCCESS" -> Color(if (colors == LightColors) "#166534" else "#86efac")
                                        "FAILED" -> Color(if (colors == LightColors) "#991b1b" else "#fca5a5")
                                        else -> Color(if (colors == LightColors) "#854d0e" else "#fcd34d")
                                    })
                                }
                            }) {
                                Text(resultStatus)
                            }
                        }

                        if (isbn != null) {
                            Div({
                                style {
                                    fontSize(13.px)
                                    color(Color(colors.textMuted))
                                }
                            }) {
                                Text("$successCount succeeded, $failureCount failed")
                            }
                        }

                        error?.let { err ->
                            Div({
                                style {
                                    fontSize(13.px)
                                    color(Color(colors.danger))
                                    marginTop(Spacing.sm)
                                }
                            }) {
                                Text("Error: $err")
                            }
                        }
                    }
                }
            }
        }

        // Close button
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.End)
                marginTop(Spacing.xl)
            }
        }) {
            Button({
                onClick { onClose?.invoke() }
                style {
                    padding(10.px, Spacing.xl)
                    fontSize(Typography.base)
                    fontWeight("500")
                    backgroundColor(Color(colors.primary))
                    color(Color("#ffffff"))
                    border(0.px)
                    borderRadius(BorderRadius.md)
                    cursor("pointer")
                }
            }) {
                Text("Close")
            }
        }
    }
}

private fun formatFileSize(bytes: Double): String {
    return when {
        bytes < 1024 -> "$bytes B"
        bytes < 1024 * 1024 -> "${(bytes / 1024).toInt()} KB"
        bytes < 1024 * 1024 * 1024 -> "${(bytes / (1024 * 1024)).toInt()} MB"
        else -> "${(bytes / (1024 * 1024 * 1024)).toInt()} GB"
    }
}
